import "./NotificationsList.css";

import React, { useState, useCallback, useEffect } from 'react'
import { Button, Card, Col, Container, Row } from 'react-bootstrap';
import { Alert, DatePicker, Pagination, Select } from 'antd';
import { Notifications } from '../../api/Notifications';
import { useToasts } from '../../contexts/toasts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import NotificationItem from "../../components/list-items/NotificationItem";
import { cloneDeep } from "lodash";
import { useAuth } from "../../contexts/auth";
import { EventEmitter } from "../../utils/EventEmitter";
import { Link } from "react-router-dom";
import { CustomBootstrapLinkType, notificationTypesOptions } from "../../utils";
import { AppNotification } from "../../models/Notification";
import AppLoader from "../../components/common/AppLoader";
import { useFullScreenLoader } from "../../contexts/fullScreenLoader";


export default function NotificationsList() {
  const [notifications, setNotifications] = useState<AppNotification[]>();
  const [range, setRange] = useState<[any, any] | null>();
  const [notificationType, setNotificationType] = useState<string>();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [total, setTotal] = useState<number>();
  const [error, setError] = useState(false);

  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();
  const { setNewNotifications } = useAuth();

  useEffect(() => {
    EventEmitter.subscribe("new-notification", (newNotification: AppNotification) => {
      setNotifications(currentNotifications => {
        if (currentPage === 1) {
          const newNotifications = cloneDeep(currentNotifications);
          newNotifications?.unshift(newNotification);
          newNotifications?.pop();
          return newNotifications;
        }
      })
      setTotal(count => count ? count + 1 : undefined);
    });
    return () => EventEmitter.unsubscribe("new-notification");
  }, [currentPage]);

  useEffect(() => {
    reset();
    // eslint-disable-next-line
  }, [range, itemsPerPage, currentPage, notificationType]);

  const reset = () => {
    setError(false);
    setNotifications(undefined);
    getNotifications();
  }

  const getNotifications = useCallback(() => {
    const params = new URLSearchParams([['page', `${currentPage}`], ['size', `${itemsPerPage}`]]);
    if (range) {
      params.set("from", range[0].toISOString());
      params.set("to", range[1].toISOString());
    }
    if (notificationType) params.set('type', notificationType);

    Notifications.getNotifications(params)
      .then(
        ({ data }) => {
          setNotifications(data.content);
          setTotal(data.total);
        },
        (err) => {
          setError(true);
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('notifications-list-error', message, `Notifications Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [currentPage, itemsPerPage, range, showToast, notificationType]);

  const readNotification = useCallback((id: number) => () => {
    Notifications.readNotification(id)
      .then(
        () => {
          setNotifications(nn => {
            const notificationsClone = cloneDeep(nn);
            const found = notificationsClone!.findIndex(n => n.id! === id);
            notificationsClone![found].read = true;
            return notificationsClone;
          });
          setNewNotifications(number => number ? number - 1 : undefined);
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('notifications-read-error', message, `Notifications Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [showToast, setNewNotifications]);

  const readAllNotifications = useCallback(() => {
    showLoader();

    Notifications.readAllNotifications()
      .then(
        () => {
          hideLoader();
          setNotifications(nn => nn!.map(n => { n.read = true; return n; }));
          setNewNotifications(0);
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('notifications-readall-error', message, `Notifications Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [showLoader, hideLoader, showToast, setNewNotifications]);

  const handleItemsPerPageChange = useCallback((page: number, size: number) => {
    setCurrentPage(page);
    setItemsPerPage(size);
  }, []);

  const renderAsyncContent = useCallback(() => {
    if (error) {
      return (
        <Alert
          type="error"
          showIcon
          message="An error occurred while retrieving notifications. Try again later"
          className="m-2"
        />
      )
    }

    if (!notifications) return <AppLoader className="py-5 my-5" justify="center" size={100} />;

    return notifications!.length === 0
      ? (
        <Alert
          type="info"
          showIcon
          message="No notifications found."
          className="m-2"
        />
      )
      : (
        <div className="notifications-wrapper">
          {notifications!.map(notification => (
            <NotificationItem key={notification.id} notification={notification} onReadNotification={readNotification(notification.id)} />
          ))}
        </div>
      )
  }, [notifications, error, readNotification]);

  return (
    <Container className="my-3">
      <Row className="align-items-center mb-3">
        <Col>
          <h5 className="mb-0">Notifications</h5>
        </Col>

        <Col sm={4} className="text-end">
          <Button variant="light" className="border px-3" size="sm" as={Link as unknown as CustomBootstrapLinkType} to="/notifications/preferences">
            <FontAwesomeIcon icon={solid('cog')} />
            <span className="ms-2">Preferences</span>
          </Button>
        </Col>
      </Row>

      <Card>
        <Card.Header className="bg-white d-flex align-items-center">
          <FontAwesomeIcon className="me-2" icon={solid('filter')} />
          <Col md={3}>
            <DatePicker.RangePicker value={range} onChange={setRange} className="w-100" />
          </Col>

          <Col md={3} className="ps-3">
            <Select
              placeholder="Select notification type"
              options={notificationTypesOptions}
              allowClear
              className="w-100"
              value={notificationType}
              onChange={setNotificationType}
            />
          </Col>

          {total !== undefined && total > itemsPerPage && (
            <Col flex="1 0" className="pe-3 text-end">
              <Pagination
                disabled={!notifications}
                total={total}
                showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
                pageSize={itemsPerPage}
                current={currentPage}
                onChange={setCurrentPage}
                onShowSizeChange={handleItemsPerPageChange}
                className="align-items-center justify-content-center"
                simple
                size="small"
              />
            </Col>
          )}

          <Button variant="light" className="ms-auto border" size="sm" onClick={readAllNotifications}>
            <FontAwesomeIcon icon={solid('check-double')} />
            <span className="ms-2">Mark all as read</span>
          </Button>
        </Card.Header>
        <Card.Body className="p-0">{renderAsyncContent()}</Card.Body>
      </Card>
    </Container>
  )
}
